import { ReactElement } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { Box } from '@mui/material';
import BigNumber from 'bignumber.js';

import { GuardButton } from 'modules/auth/components/GuardButton';
import { NumberTextField } from 'modules/common/components/NumberTextField';
import { Summary } from 'modules/common/components/Summary';
import { SummaryItem } from 'modules/common/components/SummaryItem';
import { ONE } from 'modules/common/const';
import mainTokenIcon from 'modules/common/icons/main-token-icon.svg';
import { useGlobalTranslation } from 'modules/i18n';
import { UnstakeTypeSelect } from 'modules/liquidStaking/components/UnstakeTypeSelect/UnstakeTypeSelect';
import { UnstakeType } from 'modules/liquidStaking/const';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';

import { useUnstakeForm } from '../../hooks/useUnstakeForm';
import { translation } from './translation';
import { useStyles } from './useStyles';

export interface IUnstakeFormProps {
  type: UnstakeType;
  setType: (type: UnstakeType) => void;
  onSubmit: (params: { amount: BigNumber; receiveAmount: BigNumber }) => void;
  isSubmitLoading: boolean;
}

export function UnstakeForm({
  type,
  setType,
  onSubmit,
  isSubmitLoading,
}: IUnstakeFormProps): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes } = useStyles();

  const { isDeprecated } = useLiquidStakingPool();

  const {
    balance,
    minAmount,
    isDisabled,
    receiveAmount,
    receiveForOneAmount,
    control,
    isError,
    errorMessage,
    onMaxClick,
    onSubmit: handleSubmit,
    isSubmitDisabled,
    stakeTokenName,
  } = useUnstakeForm(type);

  return (
    <form
      className={classes.root}
      onSubmit={handleSubmit(amount =>
        onSubmit({ amount: new BigNumber(amount), receiveAmount }),
      )}
    >
      <NumberTextField
        balance={balance}
        control={control as unknown as Control<FieldValues>}
        disabled={isDisabled}
        error={isError}
        helperText={errorMessage}
        label={t(keys.inputTokenLabel, { token: stakeTokenName })}
        min={minAmount}
        name="amount"
        placeholder="0"
        unit={stakeTokenName}
        variant="filled"
        onMaxClick={onMaxClick}
      />

      {!isDeprecated && (
        <UnstakeTypeSelect
          className={classes.typeSelect}
          setType={setType}
          type={type}
        />
      )}

      <Summary className={classes.summary}>
        <SummaryItem
          label={t(keys.receiveLabel)}
          value={
            <>
              <Box alt="" component="img" minWidth={16} src={mainTokenIcon} />

              <div className={classes.receiveValue}>
                {t(keys.unit.tokenValue, {
                  value: receiveAmount.isZero()
                    ? '0.00'
                    : receiveAmount.decimalPlaces(3),
                  token: t(keys.tokens.main),
                })}
              </div>
            </>
          }
        />

        <SummaryItem
          label={t(keys.exchangeRateLabel)}
          value={t(keys.rateValues, {
            input: t(keys.unit.tokenValue, {
              value: ONE.toFormat(),
              token: stakeTokenName,
            }),
            output: t(keys.unit.tokenValue, {
              value: receiveForOneAmount.decimalPlaces(3).toFormat(),
              token: t(keys.tokens.main),
            }),
          })}
        />
      </Summary>

      <GuardButton
        disabled={isSubmitDisabled}
        loading={isSubmitLoading}
        size="large"
        sx={theme => ({ marginTop: theme.spacing(6) })}
        type="submit"
        variant="outlined"
      >
        {t(keys.stake)}
      </GuardButton>
    </form>
  );
}
